import React from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
  Legend,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import { defaultTooltipConfig, defaultGridConfig } from '../../utils/chartConfig';

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend);

// Canonical broad-class colours. Keys must match BroadAssetClass.name from
// the live taxonomy. Anything not in the map falls back to an index-stable
// palette so newly-added broad classes get a sensible (non-grey) colour
// until they're added here.
const ASSET_CLASS_COLOUR_MAP = {
  'Global Alpha': '#BBCBD7',
  'Global Alpha - G': '#BBCBD7',
  'Global Alpha - D': '#A6ACB3',
  'Australian Equities': '#011E41',
  'International Equities': '#5E819C',
  'Real Estate': '#A6C6C1',
  'Infrastructure': '#337B91',
  'Cash': '#EFE6D5',
  'Government Bonds': '#F3BD48',
  'Corporate Debt': '#AA8090',
  'Private Debt': '#874564',
  'Private Equity': '#6C757D',
  'Private Infrastructure': '#127080',
  'Private Real Estate': '#34495E',
  'Real Assets (Private Market)': '#127080',
};

const FALLBACK_PALETTE = [
  '#072040', '#f0a500', '#91CF9B', '#599BE3', '#9B59B6',
  '#1ABC9C', '#E67E22', '#34495E', '#7F8C8D', '#E74C3C',
];

const AssetClassChart = React.forwardRef(({ allocation }, ref) => {
  // Filter out zero/negligible allocations, convert to %, sort descending
  const filtered = Object.entries(allocation)
    .filter(([, v]) => v > 0.001)
    .map(([label, v]) => ({
      label,
      value: parseFloat((v * 100).toFixed(1)),
    }))
    .sort((a, b) => b.value - a.value);

  const labels = filtered.map((d) => d.label);
  const values = filtered.map((d) => d.value);
  const backgroundColours = labels.map(
    (label, i) => ASSET_CLASS_COLOUR_MAP[label] || FALLBACK_PALETTE[i % FALLBACK_PALETTE.length]
  );

  const data = {
    labels,
    datasets: [
      {
        data: values,
        backgroundColor: backgroundColours,
        borderColor: backgroundColours.map((c) => c + 'CC'),
        borderWidth: 1,
        borderRadius: 3,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      title: {
        display: true,
        text: 'Asset Class Allocation (Lookthrough)',
        font: { size: 14 },
      },
      legend: {
        display: false,
      },
      tooltip: {
        ...defaultTooltipConfig,
        callbacks: {
          label: (item) => `${item.label}: ${item.parsed.y}%`,
        },
      },
    },
    scales: {
      x: {
        grid: { display: false },
        ticks: {
          autoSkip: false,
          font: { size: 10 },
          maxRotation: 0,
          minRotation: 0,
          callback: function (value) {
            const label = this.getLabelForValue(value);
            // Split long labels into multiple lines
            const words = label.split(' ');
            const lines = [];
            let line = '';
            for (const word of words) {
              if (line && (line + ' ' + word).length > 12) {
                lines.push(line);
                line = word;
              } else {
                line = line ? line + ' ' + word : word;
              }
            }
            if (line) lines.push(line);
            return lines;
          },
        },
      },
      y: {
        grid: defaultGridConfig,
        ticks: {
          callback: (value) => `${value}%`,
        },
        title: {
          display: true,
          text: 'Allocation (%)',
          font: { size: 12 },
          color: '#6c757d',
        },
      },
    },
  };

  return (
    <div style={{ height: '300px', position: 'relative' }}>
      <Bar ref={ref} key="asset-class-vertical" data={data} options={options} />
    </div>
  );
});

AssetClassChart.displayName = 'AssetClassChart';

export default AssetClassChart;
